import { useMemo } from 'react'

import BigMoney from 'apps/employer_renewals/components/costs/BigMoney'
import { InvoiceStatusBadge } from 'components/billing/InvoiceStatusBadge'
import { convertGrapeMoneyToNumber, formatMoney } from 'utils'
import { type EmployersStripeInvoice, type EmployersStripeInvoiceLineItem } from 'utils/web_api.schemas'

import { type CostBreakdownItem, CostBreakdownTable } from './CostBreakdownTable'
import { InvoiceCostBreakdownMobile } from './mobile'

interface StripeInvoiceBreakdownProps {
  invoice: EmployersStripeInvoice
}

const FEE_DESCRIPTIONS: Record<string, string> = {
  platform_fees: 'Covers the cost of offering our platform for insurance plans and payroll integrations.',
  cobra_aca_filing: 'Covers the cost of tax reporting and COBRA administration that are needed to meet healthcare regulations.',
  advisor_fee: 'Covers the cost of your dedicated benefits advisor.',
}

function getFeeDescription (description: string): string {
  const lowerDesc = description.toLowerCase()
  if (lowerDesc.includes('platform') || lowerDesc.includes('pepm')) {
    return FEE_DESCRIPTIONS.platform_fees
  }
  if (lowerDesc.includes('compliance')) {
    return FEE_DESCRIPTIONS.cobra_aca_filing
  }
  if (lowerDesc.includes('advising')) {
    return FEE_DESCRIPTIONS.advisor_fee
  }
  return FEE_DESCRIPTIONS.platform_fees
}

export function StripeInvoiceBreakdown ({ invoice }: StripeInvoiceBreakdownProps) {
  const feeItems = useMemo<CostBreakdownItem[]>(() => {
    if (!invoice?.line_items) return []
    const lineItemsArray = Array.isArray(invoice.line_items) ? invoice.line_items : [invoice.line_items]
    return lineItemsArray.map((lineItem: EmployersStripeInvoiceLineItem) => ({
      title: lineItem.description,
      description: getFeeDescription(lineItem.description),
      employeeCount: lineItem.quantity ?? null,
      perEmployeeAmount: lineItem.quantity ? convertGrapeMoneyToNumber(lineItem.unit_price) : undefined,
      amount: formatMoney(convertGrapeMoneyToNumber(lineItem.amount)),
    }))
  }, [invoice?.line_items])

  return (
    <div className='mb-6'>
      <h3 className='heading-sm mb-4'>Bill breakdown</h3>
      <div className='rounded-2xl shadow-[0_1px_4px_rgba(0,0,0,0.1)] border border-gray-200 bg-white p-2'>
        <div className='mb-4 p-6'>
          <div className='flex items-center gap-4 mb-1'>
            <span className='body-md'>Platform fees</span>
          </div>
          <div className='flex items-center gap-4'>
            <BigMoney money={convertGrapeMoneyToNumber(invoice.total)} />
            <InvoiceStatusBadge
              invoice={{ ...invoice, type: 'stripe' }}
            />
          </div>
        </div>

        <CostBreakdownTable items={feeItems} />
        <InvoiceCostBreakdownMobile items={feeItems} />
      </div>
    </div>
  )
}
